import moment from "moment";
import _ from "lodash";
import * as bankService from "@/shared/api/bankService";
import * as storeHelper from "@/shared/storeHelper";
import {
    FetchState,
    Transaction,
    TransactionSource,
    TransactionStatus,
    FetchStateKey,
    TransactionApiStatus,
    PendingReimbursement,
    ContributionType
} from "@/shared/types";
import {
    transactionGetFormattedDisplayDescription,
    transactionGetFormattedDisplayStatus
} from "@/shared/transactionHelper";
import {
    fetchStateKeyMatch,
    FetchKeyedResource
} from "@/shared/fetchStateHelper";
import { isProd } from "@/shared/env";
import { BankCustodian } from "@/admin/types";
import { defineStore } from "pinia";
import { useHsaDailyBalanceStore } from "@/shared/store/hsaDailyBalance";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { templatedFetchKeyStore } from "@/shared/store/fetchKeyedPlugin";


interface HsaPendingTransaction {
    description: string;
    amount: number;
    date: moment.Moment;
}

interface HsaPendingTransactionState {
    fetchState: FetchState;
    fetchKey: FetchStateKey;
    transactionsState: HsaPendingTransaction[];
    contributionActivityFetchState: FetchState;
    contributionActivity: Transaction[];
    contributionFetchKey: FetchStateKey;
}

const emptyState: HsaPendingTransactionState = {
    fetchState: FetchState.Uninitialized,
    fetchKey: null,
    transactionsState: [],
    contributionActivityFetchState: FetchState.Uninitialized,
    contributionActivity: [],
    contributionFetchKey: null,
};


const usePendingReimbursementSubStore = defineStore("drivewealth_pendingReimbursement", () => {
    const defaultPendingState: PendingReimbursement = {
        count: 0,
        pendingReimbursements: []
    };
    const getEmptyPendingReimbursements = () => (defaultPendingState);

    const pendingReimbursementValueSetter = (state: FetchKeyedResource<PendingReimbursement>,
                                             dto: PendingReimbursement) => {
        state.pendingReimbursements = dto.pendingReimbursements;
        state.count = dto.count;
    };
    return templatedFetchKeyStore<PendingReimbursement, any>(
        getEmptyPendingReimbursements,
        pendingReimbursementValueSetter,
        bankService.getPendingReimbursements
    );
});

export const useHsaPendingTransactionStore = defineStore("hsaPendingTransaction", {
    state: () => (_.cloneDeep(emptyState)),
    getters: {
        pendingReimbursement: () => usePendingReimbursementSubStore(),
        isLoading: state => state.fetchState === FetchState.Loading,
        creditCardPendingTrx () {
            const custodianSettingsStore = useCustodianSettingsStore();
            if (custodianSettingsStore.userCustodianSettings.userHsaCustodian !== BankCustodian.Avidia ||
                !this.balancesAreLoaded ||
                !this.transactionsState) {
                return null;
            }
            const pendingItems = [ ...this.transactionsState ];
            const sumPendingAmounts = pendingItems.reduce((total, current) => total + current.amount, 0);

            let syntheticAmount = this.availableBalance - sumPendingAmounts - this.currentBalance;
            syntheticAmount = Math.round((syntheticAmount + Number.EPSILON) * 100) / 100;

            const allowSynthetic = this.currentBalance > (this.availableBalance + sumPendingAmounts);

            if (allowSynthetic && syntheticAmount < 0) {
                return {
                    description: "Debit Card Authorization Hold Amount",
                    amount: syntheticAmount,
                    date: moment()
                };
            }

            return null;
        },
        transactions (): HsaPendingTransaction[] {
            if (this.fetchState === FetchState.Loaded) {
                return this.creditCardPendingTrx ? [ ...this.transactionsState, this.creditCardPendingTrx ]
                    : this.transactionsState;
            }

            return [];
        },
        contributionActivityIsLoaded: state => state.contributionActivityFetchState === FetchState.Loaded,
        balancesAreLoaded () {
            const hsaDailyBalanceStore = useHsaDailyBalanceStore();

            return hsaDailyBalanceStore.instantBalance.fetchState ===
                FetchState.Loaded &&
                hsaDailyBalanceStore.balanceOnFile.fetchState === FetchState.Loaded;
        },
        currentBalance () {
            const hsaDailyBalanceStore = useHsaDailyBalanceStore();
            const balanceOnFile = hsaDailyBalanceStore.balanceOnFile;
            if (balanceOnFile.fetchState === FetchState.Loaded) {
                return balanceOnFile.amount;
            }

            return 0;
        },

        availableBalance () {
            const hsaDailyBalanceStore = useHsaDailyBalanceStore();
            const instantBalance = hsaDailyBalanceStore.instantBalance;
            if (instantBalance.fetchState === FetchState.Loaded) {
                return instantBalance.amount;
            }

            return 0;
        },
        pendingReimbursementsLoaded: () => usePendingReimbursementSubStore().fetchState === FetchState.Loaded,
        pendingReimbursements: () => usePendingReimbursementSubStore().pendingReimbursements,
    },
    actions: {
        invalidateLoadedState (key: FetchStateKey): void {
            if (fetchStateKeyMatch(key, this.fetchKey)) {
                return;
            }
            const stateClone = _.cloneDeep(emptyState);
            for (const key in stateClone) {
                this[key] = stateClone[key];
            }
        },
        invalidateContributionLoadedState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.contributionFetchKey)) {
                return;
            }
            const stateClone = _.cloneDeep(emptyState);
            for (const key in stateClone) {
                this[key] = stateClone[key];
            }
        },
        setLoading (fetchKey: FetchStateKey): void {
            this.fetchState = FetchState.Loading;
            this.fetchKey = fetchKey;
        },
        setTransactions (transactions: HsaPendingTransaction[]): void {
            this.transactionsState = transactions;
            this.fetchState = FetchState.Loaded;
        },
        setContributionActivityLoading (fetchKey: FetchStateKey): void {
            this.contributionActivityFetchState = FetchState.Loading;
            this.contributionFetchKey = fetchKey;
        },
        setContributionActivity (pendingActivity: Transaction[]): void {
            this.contributionActivity = pendingActivity;
            this.contributionActivityFetchState = FetchState.Loaded;
        },
        clearPendingTransactions () {
            this.fetchState = FetchState.Uninitialized;
            this.transactionsState = [];
        },
        loadPendingTransactions (
            args: { username: string, partnerId: number }
        ): Promise<void> {

            const fetchStateKey: FetchStateKey = {
                username: args.username,
                partnerId: args.partnerId
            };

            this.invalidateLoadedState(fetchStateKey);

            this.setLoading(fetchStateKey);

            return bankService.getPendingTransactions(args.username, args.partnerId)
                .then(response => response.data.transactions)
                .then(data => {
                    if (this.fetchKey !== fetchStateKey) {
                        return;
                    }
                    const pendingItems = data
                        .filter(item => item.status === "pending" || item.status === "intraday")
                        .map(item => ({ description: item.description, amount: item.amount, date: moment(item.date) }));

                    this.setTransactions(pendingItems);
                })
                .catch(err => {
                    if (!isProd()) return;
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        fetchContributionActivity (args?: { username?: string, partnerId?: number,
            contributionType?: ContributionType }): Promise<any> {

            args = args || {};

            const fetchStateKey: FetchStateKey = {
                username: args.username,
                partnerId: args.partnerId
            };

            this.invalidateContributionLoadedState(fetchStateKey);
            this.setContributionActivityLoading(fetchStateKey);

            return bankService.getPendingContributions(args.username, args.partnerId, args.contributionType)
                .then(response => {
                    if (!response.data.contributions) {
                        return;
                    }
                    if (this.contributionFetchKey !== fetchStateKey) {
                        return;
                    }
                    const transactions = response.data.contributions.map(contribution => {
                        const transactionSource: TransactionSource = contribution.transactionType as TransactionSource;
                        const transaction: Transaction = {
                            amount: contribution.amount,
                            description: contribution.transactionType,
                            displayDescription: "",
                            moneyIncreasing: true,
                            postedDate: contribution.transactionDate,
                            source: transactionSource,
                            displaySource: contribution.displaySource,
                            status: TransactionApiStatus.Processing,
                            displayStatus: transactionGetFormattedDisplayStatus(TransactionStatus.Processing),
                            linkedBankAccount: contribution.linkedBankAccount
                        };

                        if (contribution.displayDescription) {
                            transaction.displayDescription = contribution.displayDescription;
                        } else {
                            transaction.displayDescription = transactionGetFormattedDisplayDescription(transaction);
                        }
                        return transaction;
                    });
                    this.setContributionActivity(transactions);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: false }));
        },
        loadPendingReimbursements (options: { username?: string, partnerId?: number }): Promise<any> {
            return usePendingReimbursementSubStore().fetchKeyedApiCall(options)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        }
    }
});